import React, { useEffect, useState } from "react";
import { intl } from "LocaleWrapper";

import { Button, Card, Checkbox } from "@cofano/cck";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	CircularProgress,
	Grid2 as Grid,
	Tab,
	Tabs,
	Typography,
	useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import useTabs from "components/hooks/useTabs";
import { FormattedDate  } from "utils/DateUtils";
import AccountPermissionSelect from "view/users/components/selects/AccountPermissionSelect";

import { AdminSettingsPlugin, AdminSettingsPluginValue, AdminSettingsGroup, AdminSettingsGroupField } from "view/admin/utils/admin.types";

import AdminSettingsSearch from "./AdminSettingsSearch";
import useAdminSettings from "./useAdminSettings";
import AdminSettingsChangesButton from "./AdminSettingsChangesButton";
import AdminSettingsPluginField from "./AdminSettingsPluginField";

const AdminSettings = () => {
	const theme = useTheme();

	const {
		settings,
		plugins,
		groups,
		onPluginChange,
		onFieldChange,
		onFieldAccessChange,
		changes,
		save,
	} = useAdminSettings();

	const { selectedTab, selectTab } = useTabs<number>({
		defaultSelected: 1,
		disableParams: true,
	});

	const [ searchValue, setSearchValue ] = useState("");

	const visiblePlugins = React.useMemo(() => {
		return [ ...plugins ].filter((it) => {
			if (searchValue) {
				const search = searchValue.toLowerCase();
				return it.key.toLowerCase().includes(search) || (it.description ?? "").toLowerCase().includes(search);
			} else {
				return it.categoryId === selectedTab;
			}
		});
	}, [ plugins, selectedTab, searchValue ]);

	const visibleGroups = React.useMemo(() => {
		return [ ...groups ].filter((it) => {
			if (searchValue) {
				const search = searchValue.toLowerCase();
				const groupMatches = it.name.toLowerCase().includes(search);
				const fieldMatches = (it.fields ?? []).some((field) => field.name.toLowerCase().includes(search));
				return groupMatches || fieldMatches;
			} else {
				return it.categoryId === selectedTab;
			}
		});
	}, [ groups, selectedTab, searchValue ]);

	useEffect(() => {
		const categories = settings?.categories ?? [];

		if (categories.length > 0 && !categories.some(c => c.id === selectedTab)) {
			selectTab(categories[0].id);
		}
	}, [ selectedTab, settings, selectTab ]);

	if (!settings) {
		return (
			<Box display="flex" justifyContent="center" padding={4}>
				<CircularProgress size={24} />
			</Box>
		);
	}

	return (
		<Box>
			<Box
				sx={{
					alignItems: "center",
					backgroundColor: (theme) => theme.palette.background.paper,
					borderBottom: 1,
					borderColor: "divider",
					boxShadow: "0px -2px 4px rgba(0, 0, 0, 0.1)",
					display: "flex",
					justifyContent: "space-between",
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
				p={1.5}>
				<Box>
					<Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 800 }}>
						{intl.formatMessage({ id: "ADMIN_PANEL_LAST_MODIFIED" })} <FormattedDate date={settings.timestamp} />
					</Typography>
				</Box>
				<Box sx={{ display: "flex", gap: 0.5 }}>
					{(changes.plugins.length > 0 || changes.fields.length > 0) && (
						<AdminSettingsChangesButton changes={changes} />
					)}

					<Button onClick={save} size="small" variant="text">
						{intl.formatMessage({ id: "SAVE" })}
					</Button>
				</Box>
			</Box>

			<Card disablePadding>
				<React.Fragment>
					<Box p={2}>
						<AdminSettingsSearch onSearch={(value) => setSearchValue(value)} />
					</Box>

					<Box sx={{ borderTop: 1, borderColor: "divider" }}>
						<Grid container>
							{!searchValue && (
								<Grid
									size={2}
									sx={{
										backgroundColor: theme.palette.mode === "dark" ? "rgb(30, 32, 37)" : "#f7f7f7",
										borderRight: 1,
										borderColor: "divider",
									}}>
									<Box
										sx={{
											alignSelf: "start",
											height: "fit-content",
											position: "sticky",
											top: 53,
										}}>
										<Tabs
											orientation='vertical'
											value={selectedTab}
											onChange={(_, value) => selectTab(value)}>
											{settings.categories?.map((category, index) => (
												<Tab
													label={category.name}
													value={category.id}
													sx={{
														alignItems: "start",
														minHeight: 34,
													}}
													key={index}
												/>
											))}
										</Tabs>
									</Box>
								</Grid>
							)}

							<Grid size={searchValue ? 12 : 10}>
								{visiblePlugins.map((plugin) => (
									<PluginRow
										key={plugin.key}
										plugin={plugin}
										onPluginChange={onPluginChange} />
								))}

								{visibleGroups.map((group) => (
									<FieldRow
										group={group}
										onFieldChange={onFieldChange}
										onFieldAccessChange={onFieldAccessChange}
										searchValue={searchValue}
										key={`${group.name}-${Boolean(searchValue)}`}
									/>
								))}
							</Grid>

							{visiblePlugins.length === 0 && visibleGroups.length === 0 && searchValue && (
								<Grid size={12}>
									<Alert severity='info'>{intl.formatMessage({ id: "sZeroRecords" })}</Alert>
								</Grid>
							)}
						</Grid>
					</Box>
				</React.Fragment>
			</Card>
		</Box>
	);
};

const PluginRow = ({
	plugin,
	onPluginChange,
}: {
	plugin: AdminSettingsPlugin;
	onPluginChange: (pluginKey: string, value: AdminSettingsPluginValue) => void;
}) => {
	return (
		<Grid
			container
			sx={{
				borderBottom: 1,
				borderColor: "divider",
				p: 1.5,
			}}>
			<Grid size={6}>
				<div style={{ fontSize: 12, fontWeight: 600 }}>{plugin.key}</div>
				<div style={{ fontSize: 12, fontStyle: "italic" }}>{plugin.description}</div>
			</Grid>

			<Grid size={6} sx={{ display: "flex", alignItems: "center", paddingLeft: 2 }}>
				<AdminSettingsPluginField
					plugin={plugin}
					onPluginChange={onPluginChange} />
			</Grid>
		</Grid>
	);
};

const FieldRow = ({
	group,
	onFieldChange,
	onFieldAccessChange,
	searchValue,
}: {
	group: AdminSettingsGroup;
	onFieldChange: (group: AdminSettingsGroup, field: AdminSettingsGroupField, value: boolean) => void;
	onFieldAccessChange: (group: AdminSettingsGroup, field: AdminSettingsGroupField) => void;
	searchValue: string;
}) => {
	const theme = useTheme();

	const search = searchValue.toLowerCase();
	const groupMatches = Boolean(searchValue) && group.name.toLowerCase().includes(search);

	const visibleFieldItems = !searchValue || groupMatches ?
		(group.fields ?? []) :
		(group.fields ?? []).filter((it) => it.name.toLowerCase().includes(search));

	return (
		<Grid
			container
			sx={{
				borderBottom: 1,
				borderColor: "divider",
				p: 1.5,
			}}>
			<Accordion
				defaultExpanded={Boolean(searchValue)}
				sx={{ backgroundColor: theme.palette.mode === "dark" ? "rgb(30, 32, 37)" : "#f9f9f9", width: "100%" }}>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<Typography>{group.name}</Typography>
				</AccordionSummary>

				<AccordionDetails>
					<div>
						{visibleFieldItems.map((it) => {
							return (
								<Grid
									container
									sx={{
										borderBottom: 1,
										borderColor: "divider",
										p: 1.5,
									}}
									key={it.name}>
									<Grid size={4}>
										<div style={{ fontSize: 12, fontWeight: 600 }}>{it.name}</div>
									</Grid>

									<Grid size={4}>
										<Checkbox
											checked={it.enabled ?? false}
											onChange={(event) => onFieldChange(group, it, event.target.checked)}
										/>
									</Grid>

									<Grid size={4}>
										<AccountPermissionSelect
											value={it.accessUserTypes ?? []}
											onChange={(permissions) => onFieldAccessChange(group, {
												...it,
												accessUserTypes: permissions ?? [],
											})}
											multiple
										/>
									</Grid>
								</Grid>
							);
						})}
					</div>
				</AccordionDetails>
			</Accordion>
		</Grid>
	);
};

export default AdminSettings;
